import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { authStore, type DashboardAuth } from '../../auth/authStore';
import { BulkCanvassingSessionTranscriptionStartStatus } from '../../graphql/__generated__/graphql';
import type { BulkCanvassingSessionTranscriptionFilter } from '../../graphql/__generated__/graphql';
import type { CanvassingSessionFetchImpl } from './canvassingSessionApi';
import {
  bulkCanvassingSessionTranscriptionErrorCopy,
  getBulkCanvassingSessionTranscriptionError,
  startBulkCanvassingSessionTranscription,
} from './bulkCanvassingSessionTranscriptionApi';

export type BulkCanvassingSessionTranscriptionState =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'started' }
  | { status: 'no-matches' }
  | { status: 'error'; message: string; uncertain: boolean };

type ScopedState = {
  auth: DashboardAuth | null;
  value: BulkCanvassingSessionTranscriptionState;
};

export type UseBulkCanvassingSessionTranscriptionOptions = {
  fetchImpl?: CanvassingSessionFetchImpl;
};

export type UseBulkCanvassingSessionTranscriptionResult = {
  state: BulkCanvassingSessionTranscriptionState;
  start: (filter: BulkCanvassingSessionTranscriptionFilter) => Promise<void>;
};

const subscribeAuth = (onChange: () => void) => authStore.subscribe(() => onChange());

export const useBulkCanvassingSessionTranscription = ({
  fetchImpl,
}: UseBulkCanvassingSessionTranscriptionOptions = {}): UseBulkCanvassingSessionTranscriptionResult => {
  const auth = useSyncExternalStore(subscribeAuth, authStore.getAuth);
  const api = startBulkCanvassingSessionTranscription;
  const authRef = useRef(auth);
  authRef.current = auth;
  const [scopedState, setScopedState] = useState<ScopedState>({ auth, value: { status: 'idle' } });
  const requestRef = useRef<AbortController | null>(null);
  const generationRef = useRef(0);
  const pendingRef = useRef(false);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    setScopedState({ auth, value: { status: 'idle' } });
    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
      requestRef.current?.abort();
      requestRef.current = null;
      pendingRef.current = false;
    };
  }, [auth]);

  const start = useCallback(async (filter: BulkCanvassingSessionTranscriptionFilter): Promise<void> => {
    if (pendingRef.current) return;

    const requestAuth = authRef.current;
    const controller = new AbortController();
    const generation = ++generationRef.current;
    requestRef.current = controller;
    pendingRef.current = true;
    setScopedState({ auth: requestAuth, value: { status: 'pending' } });

    const isCurrent = (): boolean =>
      mountedRef.current &&
      !controller.signal.aborted &&
      requestRef.current === controller &&
      generationRef.current === generation &&
      authRef.current === requestAuth;

    try {
      const result = await api({ filter, signal: controller.signal, fetchImpl });
      if (!isCurrent()) return;
      setScopedState({
        auth: requestAuth,
        value:
          result === BulkCanvassingSessionTranscriptionStartStatus.Started
            ? { status: 'started' }
            : result === BulkCanvassingSessionTranscriptionStartStatus.NoMatches
              ? { status: 'no-matches' }
              : {
                  status: 'error',
                  message: 'Bulk processing could not be started. Try again.',
                  uncertain: false,
                },
      });
    } catch (error: unknown) {
      if (!isCurrent()) return;
      const requestError = getBulkCanvassingSessionTranscriptionError(error);
      setScopedState({
        auth: requestAuth,
        value: {
          status: 'error',
          message: bulkCanvassingSessionTranscriptionErrorCopy(requestError),
          uncertain: requestError.uncertain,
        },
      });
    } finally {
      if (isCurrent()) {
        requestRef.current = null;
        pendingRef.current = false;
      }
    }
  }, [api, fetchImpl]);

  return {
    state: scopedState.auth === auth ? scopedState.value : { status: 'idle' },
    start,
  };
};
